<template>
  <div class="surplus-form space-y-4">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <h2 class="text-xl font-bold text-gray-800">📦 Déclarer un Surplus</h2>
      <button @click="close" class="p-2 hover:bg-gray-100 rounded-lg">
        <span class="text-2xl">✕</span>
      </button>
    </div>

    <!-- Type de denrée -->
    <div class="bg-white rounded-xl p-4 shadow-sm">
      <label class="block text-sm font-semibold text-gray-700 mb-2">📋 Type de denrée *</label>
      <select
        v-model="formData.type"
        class="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
      >
        <option value="">Sélectionner...</option>
        <option value="plat">Plat principal</option>
        <option value="entree">Entrée</option>
        <option value="accompagnement">Accompagnement</option>
        <option value="dessert">Dessert</option>
        <option value="pain">Pain</option>
        <option value="fruit">Fruit</option>
      </select>
    </div>

    <!-- Nom du plat -->
    <div class="bg-white rounded-xl p-4 shadow-sm">
      <label class="block text-sm font-semibold text-gray-700 mb-2">🍽️ Nom du plat *</label>
      <input
        type="text"
        v-model="formData.dishName"
        list="menu-du-jour-dishes"
        placeholder="Poulet rôti"
        class="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
        @change="onDishNameChange"
      />
      <datalist id="menu-du-jour-dishes">
        <option v-for="item in menuItems" :key="item.name" :value="item.name">{{ item.categoryLabel }}</option>
      </datalist>
      <p v-if="menuItems.length > 0" class="text-xs text-gray-500 mt-2">
        💡 {{ menuItems.length }} plat{{ menuItems.length > 1 ? "s" : "" }} du menu du jour proposé{{
          menuItems.length > 1 ? "s" : ""
        }}
        en suggestion.
      </p>
    </div>

    <!-- Quantité estimée -->
    <div class="bg-white rounded-xl p-4 shadow-sm">
      <label class="block text-sm font-semibold text-gray-700 mb-2">
        ⚖️ Quantité estimée * ({{ formData.quantity }} kg)
      </label>
      <input type="range" v-model="formData.quantity" min="1" max="20" class="w-full mb-2" />
      <div class="flex justify-between text-xs text-gray-500">
        <span>1 kg</span>
        <span>20 kg</span>
      </div>
    </div>

    <!-- État de conservation -->
    <div class="bg-white rounded-xl p-4 shadow-sm">
      <label class="block text-sm font-semibold text-gray-700 mb-3">📊 État de conservation *</label>
      <div class="space-y-2">
        <button
          v-for="state in conservationStates"
          :key="state.id"
          @click="formData.state = state.id"
          :class="{
            'border-blue-500 bg-blue-50 font-semibold': formData.state === state.id,
            'border-gray-200': formData.state !== state.id,
          }"
          class="w-full text-left px-4 py-3 rounded-lg border-2 transition hover:border-gray-300"
        >
          <span class="mr-3">{{ formData.state === state.id ? "●" : "○" }}</span>
          {{ state.label }}
        </button>
      </div>
    </div>

    <!-- Température mesurée -->
    <div class="bg-white rounded-xl p-4 shadow-sm">
      <label class="block text-sm font-semibold text-gray-700 mb-2">🌡️ Température mesurée</label>
      <div class="flex items-center gap-3">
        <input
          type="number"
          v-model="formData.temperature"
          placeholder="4"
          class="w-24 p-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none text-center"
        />
        <span class="text-gray-600">°C</span>
      </div>
      <p class="text-xs text-gray-500 mt-2">
        <span v-if="formData.temperature && formData.temperature <= 4" class="text-green-600 font-semibold">
          ✓ Température conforme (≤ 4°C)
        </span>
        <span v-else-if="formData.temperature > 4" class="text-orange-600 font-semibold">
          ⚠ Attention : Température supérieure à 4°C
        </span>
        <span v-else class="text-gray-400">Mesurer la température du produit</span>
      </p>
    </div>

    <!-- DLC/DLUO -->
    <div class="bg-white rounded-xl p-4 shadow-sm">
      <label class="block text-sm font-semibold text-gray-700 mb-2">📅 Date limite (DLC/DLUO)</label>
      <input
        type="date"
        v-model="formData.expiryDate"
        class="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
      />
    </div>

    <!-- Disponible jusqu'à -->
    <div class="bg-blue-50 rounded-xl p-4">
      <label class="block text-sm font-semibold text-gray-700 mb-2">⏰ Disponible jusqu'à</label>
      <p class="text-2xl font-bold text-blue-600">{{ availableUntil }}</p>
      <p class="text-xs text-gray-500 mt-1">Calculé automatiquement (fin de service + 3h)</p>
    </div>

    <!-- Photo -->
    <div class="bg-white rounded-xl p-4 shadow-sm">
      <label class="block text-sm font-semibold text-gray-700 mb-3">📸 Photo (optionnel)</label>
      <div v-if="formData.photos.length > 0" class="mb-3 flex gap-2">
        <div
          v-for="(photo, idx) in formData.photos"
          :key="idx"
          class="relative w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center"
        >
          <span class="text-3xl">📷</span>
          <button
            @click="removePhoto(idx)"
            class="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs"
          >
            ✕
          </button>
        </div>
      </div>
      <button
        @click="addPhoto"
        class="w-full bg-gray-100 hover:bg-gray-200 rounded-lg p-6 transition flex flex-col items-center gap-2"
      >
        <span class="text-4xl">📷</span>
        <span class="text-sm text-gray-600">Prendre une photo</span>
      </button>
    </div>

    <!-- Remarques -->
    <div class="bg-white rounded-xl p-4 shadow-sm">
      <label class="block text-sm font-semibold text-gray-700 mb-2">💬 Remarques</label>
      <textarea
        v-model="formData.remarks"
        placeholder="Informations complémentaires..."
        class="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none resize-none"
        rows="3"
      />
    </div>

    <!-- Allergènes -->
    <div class="bg-white rounded-xl p-4 shadow-sm">
      <label class="block text-sm font-semibold text-gray-700 mb-3">⚠️ Allergènes présents</label>
      <div class="grid grid-cols-2 gap-2">
        <button
          v-for="allergen in allergens"
          :key="allergen.id"
          @click="toggleAllergen(allergen.id)"
          :class="{
            'bg-orange-500 text-white': isAllergenSelected(allergen.id),
            'bg-gray-100 text-gray-700': !isAllergenSelected(allergen.id),
          }"
          class="px-3 py-2 rounded-lg text-sm font-medium transition"
        >
          {{ allergen.label }}
        </button>
      </div>
    </div>

    <!-- Validation -->
    <div class="bg-yellow-50 border-l-4 border-yellow-500 rounded-lg p-3">
      <p class="text-xs text-yellow-800">
        <span class="font-semibold">Important :</span>
        En publiant cette offre, vous certifiez que le surplus respecte les normes d'hygiène et de sécurité alimentaire.
      </p>
    </div>

    <!-- Bouton de publication -->
    <button
      @click="submit"
      :disabled="!isFormValid || isSubmitting"
      :class="isFormValid && !isSubmitting ? 'bg-green-500 hover:bg-green-600' : 'bg-gray-300 cursor-not-allowed'"
      class="w-full text-white rounded-lg p-4 font-semibold shadow-md transition flex items-center justify-center gap-2"
    >
      <span>✅</span>
      {{ isSubmitting ? "Publication en cours…" : "Publier l'offre" }}
    </button>
  </div>
</template>

<script>
export default {
  name: "SurplusForm",
  props: {
    canteenId: { type: Number, default: null },
    serviceId: { type: Number, default: null },
    mealDate: { type: String, default: null },
  },
  data() {
    return {
      formData: {
        type: "",
        dishName: "",
        quantity: 8,
        state: "froid",
        temperature: null,
        expiryDate: "",
        photos: [],
        remarks: "",
        allergens: [],
      },
      isSubmitting: false,
      menuItems: [],
      conservationStates: [
        { id: "froid", label: "Non sorti du froid" },
        { id: "prepare", label: "Préparé mais non servi" },
        { id: "retour", label: "Retour d'assiettes" },
      ],
      allergens: [
        { id: "gluten", label: "🌾 Gluten" },
        { id: "lait", label: "🥛 Lait" },
        { id: "oeufs", label: "🥚 Œufs" },
        { id: "poisson", label: "🐟 Poisson" },
        { id: "fruits_coque", label: "🥜 Fruits à coque" },
        { id: "soja", label: "Soja" },
        { id: "celeri", label: "Céleri" },
        { id: "moutarde", label: "Moutarde" },
      ],
    }
  },
  created() {
    this.loadMenuItems()
  },
  watch: {
    mealDate() {
      this.loadMenuItems()
    },
  },
  computed: {
    availableUntil() {
      const now = new Date()
      const endTime = new Date(now)
      endTime.setHours(17, 0, 0, 0)
      return endTime.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })
    },
    isFormValid() {
      return this.formData.type && this.formData.dishName && this.formData.quantity > 0
    },
  },
  methods: {
    close() {
      this.$emit("close")
    },
    async loadMenuItems() {
      this.menuItems = []
      if (!this.canteenId) return
      const date = this.mealDate || new Date().toISOString().split("T")[0]
      try {
        const resp = await fetch(`/api/v1/canteen/${this.canteenId}/menuItems/?date=${date}`, {
          credentials: "same-origin",
        })
        if (resp.ok) {
          const data = await resp.json()
          this.menuItems = Array.isArray(data) ? data : data.results || []
        }
      } catch {
        // silencieux : le champ reste en saisie libre si le menu est indisponible
      }
    },
    onDishNameChange() {
      // Si le plat saisi correspond à une entrée du menu du jour, on pré-remplit
      // le type de denrée à partir de sa catégorie (sans écraser un choix existant).
      if (this.formData.type) return
      const match = this.menuItems.find(
        (item) => (item.name || "").trim().toLowerCase() === this.formData.dishName.trim().toLowerCase()
      )
      if (match) {
        const mapped = this.menuCategoryToType(match.category)
        if (mapped) this.formData.type = mapped
      }
    },
    menuCategoryToType(category) {
      // Correspondance catégorie de menu → type de denrée du formulaire surplus.
      const map = {
        entree: "entree",
        plat: "plat",
        sans_viande: "plat",
        accompagnement: "accompagnement",
        produit_laitier: "dessert",
        dessert: "dessert",
      }
      return map[category] || ""
    },
    addPhoto() {
      this.formData.photos.push({ id: Date.now(), url: "photo_" + (this.formData.photos.length + 1) })
      alert("Photo ajoutée")
    },
    removePhoto(idx) {
      this.formData.photos.splice(idx, 1)
    },
    toggleAllergen(allergenId) {
      const index = this.formData.allergens.indexOf(allergenId)
      if (index > -1) {
        this.formData.allergens.splice(index, 1)
      } else {
        this.formData.allergens.push(allergenId)
      }
    },
    isAllergenSelected(allergenId) {
      return this.formData.allergens.includes(allergenId)
    },
    getCsrfToken() {
      const match = document.cookie.match(/csrftoken=([^;]+)/)
      return match ? match[1] : ""
    },
    async submit() {
      if (!this.isFormValid) return
      if (!this.canteenId) {
        alert("Aucune cantine sélectionnée.")
        return
      }
      this.isSubmitting = true
      try {
        const payload = {
          canteen: this.canteenId,
          meal_date: this.mealDate || new Date().toISOString().split("T")[0],
          items: {
            type: this.formData.type,
            dish_name: this.formData.dishName,
            quantity_kg: parseFloat(this.formData.quantity),
            state: this.formData.state,
            temperature: this.formData.temperature ? parseFloat(this.formData.temperature) : null,
            expiry_date: this.formData.expiryDate || null,
            allergens: this.formData.allergens,
            remarks: this.formData.remarks,
          },
        }
        if (this.serviceId) payload.service = this.serviceId

        const resp = await fetch(`/api/v1/canteen/${this.canteenId}/surplusOffers/`, {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json", "X-CSRFToken": this.getCsrfToken() },
          body: JSON.stringify(payload),
        })
        if (resp.ok) {
          this.$emit("close")
        } else {
          const err = await resp.json().catch(() => ({}))
          alert("Erreur lors de la publication : " + JSON.stringify(err))
        }
      } catch (e) {
        alert("Erreur réseau : " + e.message)
      } finally {
        this.isSubmitting = false
      }
    },
  },
}
</script>

<style scoped>
.space-y-4 > * + * {
  margin-top: 1rem;
}
.space-y-2 > * + * {
  margin-top: 0.5rem;
}
.gap-2 {
  gap: 0.5rem;
}
.gap-3 {
  gap: 0.75rem;
}
.grid {
  display: grid;
}
.grid-cols-2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.flex {
  display: flex;
}
.flex-col {
  flex-direction: column;
}
.items-center {
  align-items: center;
}
.justify-center {
  justify-content: center;
}
.justify-between {
  justify-content: space-between;
}
.relative {
  position: relative;
}
.absolute {
  position: absolute;
}
.-top-2 {
  top: -0.5rem;
}
.-right-2 {
  right: -0.5rem;
}
.rounded-xl {
  border-radius: 0.75rem;
}
.rounded-lg {
  border-radius: 0.5rem;
}
.rounded-full {
  border-radius: 9999px;
}
.shadow-sm {
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
}
.shadow-md {
  box-shadow: 0 2px 4px -2px rgb(0 0 0 / 0.1);
}
.border-2 {
  border-width: 2px;
}
.border-l-4 {
  border-left-width: 4px;
}
.border-gray-200 {
  border-color: #e5e7eb;
}
.hover\:border-gray-300:hover {
  border-color: #d1d5db;
}
.border-blue-500 {
  border-color: #3b82f6;
}
.border-yellow-500 {
  border-color: #eab308;
}
.bg-white {
  background-color: #ffffff;
}
.bg-blue-50 {
  background-color: #eff6ff;
}
.bg-yellow-50 {
  background-color: #fefce8;
}
.bg-gray-100 {
  background-color: #f3f4f6;
}
.bg-gray-200 {
  background-color: #e5e7eb;
}
.hover\:bg-gray-100:hover {
  background-color: #f3f4f6;
}
.hover\:bg-gray-200:hover {
  background-color: #e5e7eb;
}
.bg-gray-300 {
  background-color: #d1d5db;
}
.bg-green-500 {
  background-color: #22c55e;
}
.bg-green-600 {
  background-color: #16a34a;
}
.hover\:bg-green-600:hover {
  background-color: #16a34a;
}
.bg-red-500 {
  background-color: #ef4444;
}
.bg-orange-500 {
  background-color: #f97316;
}
.text-white {
  color: #ffffff;
}
.text-blue-600 {
  color: #2563eb;
}
.text-green-600 {
  color: #16a34a;
}
.text-orange-600 {
  color: #ea580c;
}
.text-yellow-800 {
  color: #854d0e;
}
.text-gray-400 {
  color: #9ca3af;
}
.text-gray-500 {
  color: #6b7280;
}
.text-gray-600 {
  color: #4b5563;
}
.text-gray-700 {
  color: #374151;
}
.text-gray-800 {
  color: #1f2937;
}
.font-bold {
  font-weight: 700;
}
.font-semibold {
  font-weight: 600;
}
.font-medium {
  font-weight: 500;
}
.text-xl {
  font-size: 1.25rem;
  line-height: 1.75rem;
}
.text-lg {
  font-size: 1.125rem;
  line-height: 1.75rem;
}
.text-sm {
  font-size: 0.875rem;
  line-height: 1.25rem;
}
.text-xs {
  font-size: 0.75rem;
  line-height: 1rem;
}
.text-2xl {
  font-size: 1.5rem;
  line-height: 2rem;
}
.text-3xl {
  font-size: 1.875rem;
  line-height: 2.25rem;
}
.text-4xl {
  font-size: 2.25rem;
  line-height: 2.5rem;
}
.text-left {
  text-align: left;
}
.text-center {
  text-align: center;
}
.block {
  display: block;
}
.p-2 {
  padding: 0.5rem;
}
.p-3 {
  padding: 0.75rem;
}
.p-4 {
  padding: 1rem;
}
.p-6 {
  padding: 1.5rem;
}
.px-3 {
  padding-left: 0.75rem;
  padding-right: 0.75rem;
}
.px-4 {
  padding-left: 1rem;
  padding-right: 1rem;
}
.py-2 {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}
.py-3 {
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;
}
.mb-1 {
  margin-bottom: 0.25rem;
}
.mb-2 {
  margin-bottom: 0.5rem;
}
.mb-3 {
  margin-bottom: 0.75rem;
}
.mt-1 {
  margin-top: 0.25rem;
}
.mt-2 {
  margin-top: 0.5rem;
}
.mr-3 {
  margin-right: 0.75rem;
}
.w-full {
  width: 100%;
}
.w-20 {
  width: 5rem;
}
.w-24 {
  width: 6rem;
}
.w-6 {
  width: 1.5rem;
}
.h-20 {
  height: 5rem;
}
.h-6 {
  height: 1.5rem;
}
.cursor-not-allowed {
  cursor: not-allowed;
}
.resize-none {
  resize: none;
}
.transition {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}
button {
  cursor: pointer;
  border: none;
  background: none;
}
button:not(:disabled):hover {
  opacity: 0.9;
}
input,
select,
textarea {
  font-family: inherit;
}
input:focus,
select:focus,
textarea:focus {
  outline: none;
}
.focus\:border-blue-500:focus {
  border-color: #3b82f6;
}
.focus\:outline-none:focus {
  outline: 2px solid transparent;
  outline-offset: 2px;
}
input[type="range"] {
  width: 100%;
}
</style>
